<template>
  <section>
    <div v-show="isValidToken && !isLoading" class="hero">
      <div class="hero-body container box has-background-white-bis">
        <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage" id="error-container"></error-display-component>
        <div v-if="creditApplicationCompleted" class="credit-app is-success is-flex">
          <div>
            <i class="fas fa-check-circle is-success fa-2x"></i>
          </div>
          <div class="ml-20 has-text-left">
            <span class="is-size-5 has-text-weight-semibold">Application Completed!</span><br />
            Your credit application has already been completed and submitted for processing.
          </div>
        </div>
        <div class="steps" id="stepsDemo">
          <div class="step-item is-active is-success">
            <div class="step-marker">1</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Customer" class="fal fa-user fa-lg"></i>
              </p>
              <p class="step-title" v-else>CUSTOMER</p>
            </div>
          </div>
          <div v-show="showCoAppStep" class="step-item">
            <div class="step-marker">{{ coAppInfoStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Residence" class="fal fa-user-friends fa-lg"></i>
              </p>
              <p class="step-title" v-else>CO-CUSTOMER</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-marker">{{ coAppInfoStepCount + 1 }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Residence" class="fal fa-home-alt fa-lg"></i>
              </p>
              <p class="step-title" v-else>RESIDENCE</p>
            </div>
          </div>
          <div v-show="showWorkStep" class="step-item">
            <div class="step-marker">{{ coAppInfoStepCount + 2 }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Work" class="fal fa-building fa-lg"></i>
              </p>
              <p class="step-title" v-else>WORK</p>
            </div>
          </div>
          <div v-show="showSpouseStep" class="step-item">
            <div class="step-marker">{{ spouseInfoStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Spouse" class="fal fa-user-friends fa-lg"></i>
              </p>
              <p class="step-title" v-else>SPOUSE</p>
            </div>
          </div>
          <div v-show="showSpouseResidence" class="step-item">
            <div class="step-marker">{{ spouseInfoStepCount + 1 }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none; display: inline-block; white-space: nowrap;" v-if="mobile">
                <i title="Spouse" class="fal fa-user-friends fa-lg"></i>
                <i title="Residence" class="fal fa-home-alt fa-sx"></i>
              </p>
              <p class="step-title" v-else>SPOUSE RESIDENCE</p>
            </div>
          </div>
          <div v-show="showSpouseWork" class="step-item">
            <div class="step-marker">{{ spouseWorkStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none; display: inline-block; white-space: nowrap;" v-if="mobile">
                <i title="Spouse" class="fal fa-user-friends fa-lg"></i>
                <i title="Work" class="fal fa-building fa-sx"></i>
              </p>
              <p class="step-title" v-else>SPOUSE WORK</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-marker">{{ documentStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Documents" class="fal fa-id-card fa-lg"></i>
              </p>
              <p class="step-title" v-else>DOCUMENTS</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-marker">{{ documentStepCount + 1}}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Terms" class="fal fa-file-signature fa-lg"></i>
              </p>
              <p class="step-title" v-else>TERMS</p>
            </div>
          </div>

          <div class="steps-content">
            <div class="step-content">
              <div class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Please complete your credit application
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isFirstNameValid && isLastNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="currentCustomer" @submit.prevent="validateBeforeSubmit" class="has-text-centered is-active" data-vv-scope="step1">
                <section class="has-text-left box">
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">First Name</label>
                        <div class="control">
                          <input name="firstName" :disabled="true" :class="{ 'input': true }" type="text" v-model="currentCustomer.firstName">
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Last Name</label>
                        <div class="control">
                          <input name="lastName" :disabled="true" :class="{ 'input': true }" type="text" v-model="currentCustomer.lastName">
                        </div>
                      </div>
                    </div>
                    <div class="column is-12">
                      <label class="form-info">Please Note: If you would like to change your first and last name, please contact your sales person for assistance.</label>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Date of Birth<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <el-date-picker
                            name="step1.dateOfBirth"
                            v-validate="{ 'required': true, 'minimum_age_18': true }"
                            :class="{ 'is-danger': errors.has('step1.dateOfBirth') }"
                            data-vv-scope="step1"
                            data-vv-as="date of birth"
                            size="default"
                            prefix-icon="none"
                            type="date"
                            format="MM/dd/yyyy"
                            placeholder="Click to select..."
                            v-model="currentCustomer.dateOfBirth"
                            :picker-options="datePickerOptions"
                            :default-value="defaultDate"
                            :clearable="false"
                            style="width: 100%;">
                          </el-date-picker>
                          <span v-show="errors.has('step1.dateOfBirth')" class="help is-danger">
                            <i v-show="errors.has('step1.dateOfBirth')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.dateOfBirth') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-calendar-alt default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field" v-if="currentSsn && currentSsn !== ''">
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input v-if="canEditSsn"
                            name="step1.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11 }"
                            :class="{ 'input': true, 'is-danger': errors.has('step1.ssn') }"
                            data-vv-scope="step1"
                            type="text"
                            v-model="currentCustomer.ssn"
                            v-mask="'###-##-####'">
                          <span v-else class="field is-size-7">{{ currentSsn }}</span>
                          <span v-show="errors.has('step1.ssn')" class="help is-danger">
                            <i v-show="errors.has('step1.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.ssn') }}
                          </span>
                        </div>
                        <div class="control is-size-7">
                          <b-checkbox
                            :native-value="false"
                            name="chkEditSsn"
                            type="is-info"
                            v-model="canEditSsn">
                            Edit SSN
                          </b-checkbox>
                        </div>
                      </div>
                      <div class="field" v-else>
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input
                            name="step1.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11 }"
                            :class="{ 'input': true, 'is-danger': errors.has('step1.ssn') }"
                            data-vv-scope="step1"
                            type="text"
                            v-model="currentCustomer.ssn"
                            placeholder="000-00-0000"
                            v-mask="'###-##-####'">
                          <span v-show="errors.has('step1.ssn')" class="help is-danger">
                            <i v-show="errors.has('step1.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.ssn') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="columns is-multiline">
                    <div class="column is-12">
                      <div class="field">
                        <label class="label">Email<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="email" v-validate="{ 'required': true, 'email': true, 'max': 50 }" :class="{ 'input': true, 'is-danger': errors.has('step1.email') }" data-vv-scope="step1" data-vv-as="email" type="email" placeholder="Email" v-model="currentCustomer.email">
                          <span v-show="errors.has('step1.email')" class="help is-danger">
                            <i v-show="errors.has('step1.email')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.email') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-envelope default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Home Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="home" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step1.home') }" data-vv-scope="step1" data-vv-as="home phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.home">
                          <span v-show="errors.has('step1.home')" class="help is-danger">
                            <i v-show="errors.has('step1.home')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.home') }}
                          </span>
                          <!-- <div v-show="!isHomePhoneValid.valid" v-validate="!isHomePhoneValid.valid" class="help is-danger">
                            <input type="hidden" data-vv-as="home" name="step1.home" id="home" v-model="isHomePhoneValid.errorCode" data-vv-scope="step1"/>
                            <i class="fas fa-exclamation-triangle"></i>{{ isHomePhoneValid.errorMessage }}
                          </div> -->
                          <span class="icon is-small is-right">
                            <i class="fal fa-phone-volume default-blue"></i>
                          </span>
                          <pop-fill-control title="Click to restore saved phone number" :fillableValue="originalHomePhone" :onFillAction="onRestoreHomePhone" v-if="!hasViewed"></pop-fill-control>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Mobile Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="mobile" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step1.mobile') }" data-vv-scope="step1" data-vv-as="mobile phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.mobile">
                          <span v-show="errors.has('step1.mobile')" class="help is-danger">
                            <i v-show="errors.has('step1.mobile')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.mobile') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-mobile-android default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">    <!-- disable if co customer is spouse -->
                        <label class="label">Marital Status<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.maritalStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.maritalStatus') }"
                            data-vv-scope="step1"
                            data-vv-as="marital status"
                            placeholder="Select Marital Status"
                            label="name"
                            track-by="id"
                            :options="definedTypes.customerMaritalStatus.options"
                            :searchable="true"
                            v-model="selectedMaritalStatus"
                            :disabled="isCoApplicantSpouse">
                          </multiselect>
                          <span v-show="errors.has('step1.maritalStatus')" class="help is-danger">
                            <i v-show="errors.has('step1.maritalStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.maritalStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Employment Status<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.employmentStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.employmentStatus') }"
                            data-vv-scope="step1"
                            data-vv-as="employment status"
                            placeholder="Select Employment Status"
                            label="altName"
                            track-by="id"
                            :options="definedTypes.employmentStatus.options"
                            :searchable="true"
                            v-model="selectedEmploymentStatus">
                          </multiselect>
                          <span v-show="errors.has('step1.employmentStatus')" class="help is-danger">
                            <i v-show="errors.has('step1.employmentStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.employmentStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="columns is-multiline" v-if="!isCustomerCoApplicant">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Co-Applicant?<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.relationshipStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.relationshipStatus') }"
                            data-vv-scope="step1"
                            data-vv-as="co-applicant relationship status"
                            placeholder="Select Co-Applicant Relationship Status"
                            label="altName"
                            track-by="id"
                            :options="definedTypes.relationshipStatus.options"
                            :searchable="true"
                            v-model="selectedRelationshipStatus">
                          </multiselect>
                          <span v-show="errors.has('step1.relationshipStatus')" class="help is-danger">
                            <i v-show="errors.has('step1.relationshipStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.relationshipStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </form>
              <form v-if="isPrimaryPhoneAreaCodeValid.errorCode === 0 || isPrimaryMobilePhoneAreaCodeValid.errorCode === 0" class="has-text-centered" data-vv-scope="step1">
                <section class="has-text-left">
                  <div v-show="errors.has('step1.homeAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="homeAreaCode" name="step1.homeAreaCode" id="homeAreaCode" v-validate="'required|min_value:1'" v-model="isPrimaryPhoneAreaCodeValid.errorCode" data-vv-scope="step1"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isPrimaryPhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step1.mobileAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="mobileAreaCode" name="step1.mobileAreaCode" id="mobileAreaCode" v-validate="'required|min_value:1'" v-model="isPrimaryMobilePhoneAreaCodeValid.errorCode" data-vv-scope="step1"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isPrimaryMobilePhoneAreaCodeValid.errorMessage }}
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showCoAppStep" class="step-content">
              <div v-if="showCoAppStep" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide Co-Applicant Details
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isFirstNameValid && isLastNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showCoAppStep" class="has-text-centered" data-vv-scope="step2">
                <section class="has-text-left box coApplicantContainer">
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">First Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control capitilized">
                          <input name="step2.coFirstName" v-validate="{ 'required': true, 'max': 15 }" :class="{ 'input': true, 'is-danger': errors.has('step2.coFirstName') }" data-vv-scope="step2" data-vv-as="co-applicant first name" type="text" placeholder="First Name" v-model="currentCustomer.coApplicant.firstName">
                          <span v-show="errors.has('step2.coFirstName')" class="help is-danger">
                            <i v-show="errors.has('step2.coFirstName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coFirstName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Last Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control capitilized">
                          <input name="step2.coLastName" v-validate="{ 'required': true, 'max':25 }" :class="{ 'input': true, 'is-danger': errors.has('step2.coLastName') }" data-vv-scope="step2" data-vv-as="co-applicant last name" type="text" placeholder="Last Name" v-model="currentCustomer.coApplicant.lastName">
                          <span v-show="errors.has('step2.coLastName')" class="help is-danger">
                            <i v-show="errors.has('step2.coLastName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coLastName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Email<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="coEmail" v-validate="{ 'required': true, 'email': true, 'max': 50 }" :class="{ 'input': true, 'is-danger': errors.has('step2.coEmail') }" data-vv-scope="step2" data-vv-as="co-applicant email" type="email" placeholder="Email" v-model="currentCustomer.coApplicant.email">
                          <span v-show="errors.has('step2.coEmail')" class="help is-danger">
                            <i v-show="errors.has('step2.coEmail')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coEmail') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-envelope default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Date of Birth<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <el-date-picker
                            name="step2.coDateOfBirth"
                            v-validate="{ 'required': true, 'minimum_age_18': true }"
                            :class="{ 'is-danger': errors.has('step2.dateOfBirth') }"
                            data-vv-scope="step2"
                            data-vv-as="date of birth"
                            size="default"
                            prefix-icon="none"
                            type="date"
                            format="MM/dd/yyyy"
                            placeholder="Click to select..."
                            v-model="currentCustomer.coApplicant.dateOfBirth"
                            :picker-options="datePickerOptions"
                            :default-value="defaultDate"
                            :clearable="false"
                            style="width: 100%;">
                          </el-date-picker>
                          <span v-show="errors.has('step2.coDateOfBirth')" class="help is-danger">
                            <i v-show="errors.has('step2.coDateOfBirth')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coDateOfBirth') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-calendar-alt default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Home Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="coHome" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step2.coHome') }" data-vv-scope="step2" data-vv-as="co-applicant home phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.coApplicant.home">
                          <span v-show="errors.has('step2.coHome')" class="help is-danger">
                            <i v-show="errors.has('step2.coHome')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coHome') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-phone-volume default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Mobile Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="coMobile" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step2.coMobile') }" data-vv-scope="step2" data-vv-as="co-applicant mobile phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.coApplicant.mobile">
                          <span v-show="errors.has('step2.coMobile')" class="help is-danger">
                            <i v-show="errors.has('step2.coMobile')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step2.coMobile') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-mobile-android default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </form>
              <form v-if="showCoAppStep && (isCoAppPhoneAreaCodeValid.errorCode === 0 || isCoAppMobilePhoneAreaCodeValid.errorCode === 0)" class="has-text-centered" data-vv-scope="step2">
                <section class="has-text-left">
                  <div v-show="errors.has('step2.coAppHomeAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="coAppHomeAreaCode" name="step2.coAppHomeAreaCode" id="coAppHomeAreaCode" v-validate="'required|min_value:1'" v-model="isCoAppPhoneAreaCodeValid.errorCode" data-vv-scope="step2"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isCoAppPhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step2.coAppMobileAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="coAppMobileAreaCode" name="step2.coAppMobileAreaCode" id="coAppMobileAreaCode" v-validate="'required|min_value:1'" v-model="isCoAppMobilePhoneAreaCodeValid.errorCode" data-vv-scope="step2"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isCoAppMobilePhoneAreaCodeValid.errorMessage }}
                  </div>
                </section>
              </form>
            </div>

            <div class="step-content">
              <div class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide 3 Years of Residence History - Most recent first
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isFirstNameValid && isLastNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form class="has-text-centered" data-vv-scope="step3">
                <div v-show="errors.has('step3.primaryAddress')" class="notification is-warning">
                    <input type="hidden" data-vv-as="primaryAddress" name="step3.primaryAddress" id="primaryAddress" v-validate="'required|min_value:1'" v-model="isPrimaryAddressValid.errorCode" data-vv-scope="step3"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isPrimaryAddressValid.errorMessage }}
                </div>
                <section class="has-text-left">
                  <div v-for="(address, index) in addresses" :key="address.id">
                    <address-capture v-model="addresses[index]" :addressIndex="index" :originalAddressList="previousCustomerAddresses" :useValidator="thisValidator" v-on:primary-address-set="onAutoSelectPrimaryAddress" v-on:unselect-other="onUnselectNonPrimary" v-on:on-remove-address="removeAddress" scope="step3" :required="true"></address-capture>
                    <br />
                  </div>
                  <div v-show="errors.has('step3.residenceTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="residenceTotalMonths" name="step3.residenceTotalMonths" id="residenceTotalMonths" v-validate="'required|min_value:1'" v-model="isResidenceMonthsValid.errorCode" data-vv-scope="step3"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isResidenceMonthsValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer" v-if="!creditApplicationCompleted">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addAddress({})">Add</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showWorkStep" class="step-content">
              <div v-if="showWorkStep" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    {{ workHistoryHeading }} - {{ workHistorySubHeading }}
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isFirstNameValid && isLastNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showWorkStep" class="has-text-centered" data-vv-scope="step4">
                <section class="has-text-left">
                  <div v-for="(history, index) in workHistory" :key="history.id">
                    <work-history-capture v-model="workHistory[index]" :employmentStatusType="selectedEmploymentStatus ? selectedEmploymentStatus.id : 0" scope="step4" :required="true"></work-history-capture>
                    <br/>
                  </div>
                  <div v-show="errors.has('step4.workHistoryTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="workHistoryTotalMonths" name="step4.workHistoryTotalMonths" id="workHistoryTotalMonths" v-validate="'required|min_value:1'" v-model="isWorkHistoryMonthsValid.errorCode" data-vv-scope="step4"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isWorkHistoryMonthsValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step4.workHistoryEmployerName')" class="notification is-warning">
                    <input type="hidden" data-vv-as="workHistoryEmployerName" name="step4.workHistoryEmployerName" id="workHistoryEmployerName" v-validate="'required|min_value:1'" v-model="isWorkHistoryEmployerValid.errorCode" data-vv-scope="step4"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isWorkHistoryEmployerValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step4.workHistoryOfficeNumber')" class="notification is-warning">
                    <input type="hidden" data-vv-as="workHistoryOfficeNumber" name="step4.workHistoryOfficeNumber" id="workHistoryOfficeNumber" v-validate="'required|min_value:1'" v-model="isWorkHistoryPhoneAreaCodeValid.errorCode" data-vv-scope="step4"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isWorkHistoryPhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer" v-if="!creditApplicationCompleted">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addWorkHistory({})">Add</b-button>
                    <b-button icon-left="fa fa-lg fa-minus-circle" class="customButtons is-danger" type="button" inverted v-if="showRemoveWorkHistory" @click="removeWorkHistory({})">Remove</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showSpouseStep" class="step-content">
              <div v-if="showSpouseStep" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide Spouse Details
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isFirstNameValid && isLastNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showSpouseStep" class="has-text-centered" data-vv-scope="step5">
                <section class="has-text-left box">
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">First Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control capitilized">
                          <input name="step5.firstName" v-validate="{ 'required': true, 'max': 15 }" :class="{ 'input': true, 'is-danger': errors.has('step5.firstName') }" data-vv-scope="step5" data-vv-as="first name" type="text" placeholder="First Name" v-model="currentCustomer.spouse.firstName">
                          <span v-show="errors.has('step5.firstName')" class="help is-danger">
                            <i v-show="errors.has('step5.firstName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.firstName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Last Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control capitilized">
                          <input name="step5.lastName" v-validate="{ 'required': true, 'max':25 }" :class="{ 'input': true, 'is-danger': errors.has('step5.lastName') }" data-vv-scope="step5" data-vv-as="last name" type="text" placeholder="Last Name" v-model="currentCustomer.spouse.lastName">
                          <span v-show="errors.has('step5.lastName')" class="help is-danger">
                            <i v-show="errors.has('step5.lastName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.lastName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Date of Birth<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <el-date-picker
                            name="step5.sDateOfBirth"
                            v-validate="{ 'required': true, 'minimum_age_18': true }"
                            :class="{ 'is-danger': errors.has('step5.sDateOfBirth') }"
                            data-vv-scope="step5"
                            data-vv-as="date of birth"
                            size="large"
                            prefix-icon="none"
                            type="date"
                            format="MM/dd/yyyy"
                            placeholder="Click to select..."
                            v-model="currentCustomer.spouse.dateOfBirth"
                            :picker-options="datePickerOptions"
                            :default-value="defaultDate"
                            :clearable="false"
                            style="width: 100%;">
                          </el-date-picker>
                          <span v-show="errors.has('step5.sDateOfBirth')" class="help is-danger">
                            <i v-show="errors.has('step5.sDateOfBirth')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.sDateOfBirth') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-calendar-alt default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field" v-if="currentSpouseSsn && currentSpouseSsn !== ''">
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input v-if="canEditSpouseSsn"
                            name="step5.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11 }"
                            :class="{ 'input': true, 'is-danger': errors.has('step5.ssn') }"
                            data-vv-scope="step5"
                            type="text"
                            v-model="currentCustomer.spouse.ssn"
                            v-mask="'###-##-####'">
                          <span v-else class="field is-size-7">{{ currentSpouseSsn }}</span>
                          <span v-show="errors.has('step5.ssn')" class="help is-danger">
                            <i v-show="errors.has('step5.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.ssn') }}
                          </span>
                        </div>
                        <div class="field is-size-7 is-pulled-left mt-10">
                          <b-checkbox
                            :native-value="false"
                            name="chkEditSsn"
                            type="is-info"
                            v-model="canEditSpouseSsn">
                            edit SSN
                          </b-checkbox>
                        </div>
                      </div>
                      <div class="field" v-else>
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input
                            name="step5.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11 }"
                            :class="{ 'input': true, 'is-danger': errors.has('step5.ssn') }"
                            data-vv-scope="step5"
                            type="text"
                            v-model="currentCustomer.spouse.ssn"
                            placeholder="000-00-0000"
                            v-mask="'###-##-####'">
                          <span v-show="errors.has('step5.ssn')" class="help is-danger">
                            <i v-show="errors.has('step5.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.ssn') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="columns is-multiline">
                    <div class="column is-12">
                      <div class="field">
                        <label class="label">Email<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="email" v-validate="{ 'required': true, 'email': true, 'max': 50 }" :class="{ 'input': true, 'is-danger': errors.has('step5.email') }" data-vv-scope="step5" data-vv-as="email" type="email" placeholder="Email" v-model="currentCustomer.spouse.email">
                          <span v-show="errors.has('step5.email')" class="help is-danger">
                            <i v-show="errors.has('step5.email')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.email') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-envelope default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Home Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="home" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step5.home') }" data-vv-scope="step5" data-vv-as="home phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.spouse.home">
                          <span v-show="errors.has('step5.home')" class="help is-danger">
                            <i v-show="errors.has('step5.home')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.home') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-phone-volume default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Mobile Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="mobile" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step5.mobile') }" data-vv-scope="step5" data-vv-as="mobile phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.spouse.mobile">
                          <span v-show="errors.has('step5.mobile')" class="help is-danger">
                            <i v-show="errors.has('step5.mobile')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.mobile') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-mobile-android default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Employment Status<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step5.employmentStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step5.employmentStatus') }"
                            data-vv-scope="step5"
                            data-vv-as="employment status"
                            placeholder="Select Employment Status"
                            label="altName"
                            track-by="id"
                            :options="definedTypes.employmentStatus.options"
                            :searchable="true"
                            v-model="selectedSpouseEmploymentStatus">
                          </multiselect>
                          <span v-show="errors.has('step5.employmentStatus')" class="help is-danger">
                            <i v-show="errors.has('step5.employmentStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step5.employmentStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </form>
              <form v-if="showSpouseStep && (isSpousePhoneAreaCodeValid.errorCode === 0 || isSpouseMobilePhoneAreaCodeValid.errorCode === 0)" class="has-text-centered" data-vv-scope="step5">
                <section class="has-text-left">
                  <div v-show="errors.has('step5.spouseHomeAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseHomeAreaCode" name="step5.spouseHomeAreaCode" id="spouseHomeAreaCode" v-validate="'required|min_value:1'" v-model="isSpousePhoneAreaCodeValid.errorCode" data-vv-scope="step5"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpousePhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step5.spouseMobileAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseMobileAreaCode" name="step5.spouseMobileAreaCode" id="spouseMobileAreaCode" v-validate="'required|min_value:1'" v-model="isSpouseMobilePhoneAreaCodeValid.errorCode" data-vv-scope="step5"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseMobilePhoneAreaCodeValid.errorMessage }}
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showSpouseResidence" class="step-content">
              <div v-if="showSpouseResidence" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide 3 Years of Residence History - Most recent first
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isFirstNameValid && isLastNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showSpouseResidence" class="has-text-centered" data-vv-scope="step6">
                <div v-show="errors.has('step6.spousePrimaryAddress')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spousePrimaryAddress" name="step6.spousePrimaryAddress" id="spousePrimaryAddress" v-validate="'required|min_value:1'" v-model="isSpousePrimaryAddressValid.errorCode" data-vv-scope="step6"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpousePrimaryAddressValid.errorMessage }}
                </div>
                <section class="has-text-left">
                  <div v-for="(address, index) in spouseAddresses" :key="address.id">
                    <address-capture v-model="spouseAddresses[index]" :useValidator="thisValidator" :isSpouse="true" v-on:primary-address-set="onAutoSelectSpousePrimaryAddress" v-on:unselect-other="onUnselectSpouseNonPrimary" v-on:on-remove-address="removeAddress" :addressIndex="index" scope="step6" :required="true"></address-capture>
                    <br/>
                  </div>
                  <div v-show="errors.has('step6.spouseResidenceTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseResidenceTotalMonths" name="step6.spouseResidenceTotalMonths" id="spouseResidenceTotalMonths" v-validate="'required|min_value:1'" v-model="isSpouseResidenceMonthsValid.errorCode" data-vv-scope="step6"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseResidenceMonthsValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer" v-if="!creditApplicationCompleted">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addAddress({ spouseAddress: true })">Add</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showSpouseWork" class="step-content">
              <div v-if="showSpouseWork" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    {{ spouseWorkHistoryHeading }} - {{ spouseWorkHistorySubHeading }}
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isFirstNameValid && isLastNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showSpouseWork" class="has-text-centered" data-vv-scope="step7">
                <section class="has-text-left">
                  <div v-for="(history, index) in spouseWorkHistory" :key="history.id">
                    <work-history-capture v-model="spouseWorkHistory[index]" :employmentStatusType="selectedSpouseEmploymentStatus ? selectedSpouseEmploymentStatus.id : 0" scope="step7" :required="true"></work-history-capture>
                    <br/>
                  </div>
                  <div v-show="errors.has('step7.spouseWorkHistoryTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseWorkHistoryTotalMonths" name="step7.spouseWorkHistoryTotalMonths" id="spouseWorkHistoryTotalMonths" v-validate="'required|min_value:1'" v-model="isSpouseWorkHistoryMonthsValid.errorCode" data-vv-scope="step7"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseWorkHistoryMonthsValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step7.spouseWorkHistoryEmployerName')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseWorkHistoryEmployerName" name="step7.spouseWorkHistoryEmployerName" id="spouseWorkHistoryEmployerName" v-validate="'required|min_value:1'" v-model="isSpouseWorkHistoryEmployerValid.errorCode" data-vv-scope="step7"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseWorkHistoryEmployerValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step7.spouseWorkHistoryOfficeNumber')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseWorkHistoryOfficeNumber" name="step7.spouseWorkHistoryOfficeNumber" id="spouseWorkHistoryOfficeNumber" v-validate="'required|min_value:1'" v-model="isSpouseWorkHistoryPhoneAreaCodeValid.errorCode" data-vv-scope="step7"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseWorkHistoryPhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer" v-if="!creditApplicationCompleted">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addWorkHistory({spouseWorkHistory: true})">Add</b-button>
                    <b-button icon-left="fa fa-lg fa-minus-circle" class="customButtons is-danger" type="button" inverted v-if="showRemoveSpouseWorkHistory" @click="removeWorkHistory({spouseWorkHistory: true})">Remove</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div class="step-content">
              <div class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Please provide driver's license and insurance card
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isFirstNameValid && isLastNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToBusiness">Switch to business app</b-button>
                  </div>
                </div>
              </div>
              <form class="has-text-centered" data-vv-scope="step8">
                <section class="has-text-left">
                  <div class="is-full" v-if="!creditApplicationCompleted">
                    <div class="field is-full">
                      <file-selector v-model="driversPhotoFiles" label="Drivers License" :multiple="false" :token="this.token" scope="step8" destinationUrl="/upload/receive"></file-selector>
                    </div>
                    <div class="field is-full">
                      <file-selector v-model="insurancePhotoFiles" label="Insurance Card" :multiple="false" :token="this.token" scope="step8" destinationUrl="/upload/receive"></file-selector>
                    </div>
                  </div>
                  <div v-else>
                    <div class="field is-full" v-if="hasDriversImage">
                      <file-selector v-model="driversPhotoFiles" label="Drivers License" :multiple="false" :token="this.token" scope="step8" :required="false" :isBoxed="false" :enabled="false" :showFileOnly="true" destinationUrl="/upload/receive"></file-selector>
                    </div>
                    <div v-else>
                      <label class="label">Drivers License</label>
                      <figure class="image is-100x100">
                        <img :src="defaultDriversImage"/>
                      </figure>
                    </div>
                    <div class="field is-full" v-if="hasInsuranceImage">
                      <file-selector v-model="insurancePhotoFiles" label="Insurance Card" :multiple="false" :token="this.token" scope="step8" :required="false" :isBoxed="false" :enabled="false" :showFileOnly="true" destinationUrl="/upload/receive"></file-selector>
                    </div>
                    <div v-else>
                      <label class="label">Insurance Card</label>
                      <i class="fa fa-credit-card fa-5x" aria-hidden="true" style="color:#C2C2C2; margin-left:10px"></i>
                    </div>
                  </div>
                </section>
              </form>
            </div>

            <div class="step-content has-text-centered">
              <form class="has-text-centered" data-vv-scope="stepFinal">
                <div class="box" v-observe-visibility="visibilityChanged">
                  <div class="field notification is-aligned-left ml-20">
                    <terms-control ref="termsControl" :currentCustomer="currentCustomer"></terms-control>
                  </div>
                  <div class="field notification is-aligned-left ml-20" v-if="communityPropertyDisclosureRequired">
                    <property-disclosure-control ref="copControl" :currentCustomer="currentCustomer"></property-disclosure-control>
                  </div>
                  <div class="field notification is-aligned-left ml-20" v-if="hasCoApplicant">
                    <regulation-b-control ref="regBControl" :currentCustomer="currentCustomer"></regulation-b-control>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div class="steps-actions">
            <div class="steps-action">
              <b-button type="button" icon-left="chevron-left" data-nav="previous" class="is-info" :disabled="isSaving">Previous</b-button>
            </div>
            <div class="steps-action">
              <b-button type="button" icon-right="chevron-right" data-nav="next" class="next-step is-info" @click="validateStep">Next</b-button>
            </div>
            <div class="steps-action" v-if="termsVisible && !creditApplicationCompleted">
              <a class="button next-step is-success" type="submit" @click="validateBeforeSubmitAllScoped" :class="{'is-loading': isSaving}" :disabled="isSaving">Submit</a>
            </div>
          </div>
          <div class="container" v-if="isValidToken && isFirstNameValid && isLastNameValid && isEmailValid && !creditApplicationCompleted">
            <b-button type="button" class="is-info is-light" @click="navigateToBusiness">Switch to business app</b-button>
            <br />
            <br />
            <b-button type="button" class="is-info is-light" @click="saveAndContinueLater" :disabled="isSaving">Save and continue later</b-button>
          </div>
        </div>
      </div>
    </div>
    <customer-lookup-control ref="customer_lookup" :isLead="false" :token="token" v-if="currentCustomer.coApplicant && !currentCustomer.coApplicant.email" :currentCustomer="currentCustomer.coApplicant" :useValidator="thisValidator" :isCoAppSearch="true" v-on:on-load-lead="onLoadCoApplicant" v-on:on-no-lead="onNoCoApplicant"></customer-lookup-control>
    <div v-show="!isValidToken && isLoading" class="hero">
      <div class="hero-body container">
        <div class="container is-fluid">
          <div class="columns is-vcentered loadingContainer">
            <div class="column is-12 has-text-centered has-text-weight-semi-bold">
              <img src="@/assets/ajax-loader.gif">
              <div v-if="!loadingBaseData">Verifying your identity....</div>
              <div v-if="loadingBaseData">Loading base data....</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-show="!isValidToken && !isLoading" class= "hero">
      <div class="hero-body container"></div>
    </div>
    <location-control v-model="isLocationModalActive"></location-control>
  </section>
</template>

<script>
import moment from 'moment'
import customerService from '@/services/customerService'
import customerMixin from '@/mixins/customer/index'
import FileSelector from '@/components/generic/FileSelector.vue'
import utilitiesMixin from '@/mixins/generic/utilities'
import LocationControl from '@/components/generic/customer/LocationControl'
import bulmaSteps from 'bulma-steps/src/js/bulma-steps'
import PopFillControl from '@/components/generic/PopFillControl'
import customerLookupControl from '@/components/generic/CustomerLookupControl'

export default {
  name: 'CreditApplication',
  inject: {
    $validator: '$validator'
  },
  props: {
    token: {
      type: String,
      default: ''
    }
  },
  components: {
    'file-selector': FileSelector,
    'location-control': LocationControl,
    'pop-fill-control': PopFillControl,
    'customer-lookup-control': customerLookupControl
  },
  mixins: [customerMixin, utilitiesMixin],
  data () {
    return {
      isMobile: window.innerWidth <= 700,
      isLocationModalActive: false,
      isValidToken: false,
      driversPhotoFiles: [],
      insurancePhotoFiles: [],
      reg: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
      currentUrl: '',
      geoLocationEnabled: false,
      locationModalShown: false,
      currentCustomer: {
        coApplicant: {
          id: 0,
          dealerId: 0,
          type: 'Individual',
          status: 'Active',
          firstName: null,
          lastName: null,
          email: null,
          mobile: null,
          home: null,
          dateOfBirth: null
        }
      },
      canEditSsn: false,
      currentSsn: '',
      currentSpouseSsn: '',
      canEditSpouseSsn: false,
      originalHomePhone: '',
      currentCustomerId: 0,
      previousCustomerAddresses: []
    }
  },
  computed: {
    isFirstNameValid: function () {
      return this.currentCustomer && (this.currentCustomer.firstName && this.currentCustomer.firstName !== '')
    },
    isLastNameValid: function () {
      return this.currentCustomer && (this.currentCustomer.lastName && this.currentCustomer.lastName !== '')
    },
    isEmailValid: function () {
      return this.currentCustomer && (this.currentCustomer.email && this.currentCustomer.email !== '') && this.reg.test(this.currentCustomer.email)
    },
    defaultDriversImage: function () {
      var defaultImage = require('@/assets/drivers-placeholder.png')
      return defaultImage
    },
    hasDriversImage: function () {
      return this.currentCustomer && this.currentCustomer.driversFileLocation && this.currentCustomer.driversFileLocation !== ''
    },
    hasInsuranceImage: function () {
      return this.currentCustomer && this.currentCustomer.insuranceFileLocation && this.currentCustomer.insuranceFileLocation !== ''
    },
    documentStepCount: function () {
      let stepCount = 8

      if (!this.showCoAppStep) {
        stepCount--
      }

      if (!this.showWorkStep) {
        stepCount--
      }

      if (!this.showSpouseStep) {
        stepCount--
      }

      if (!this.showSpouseResidence) {
        stepCount--
      }

      if (!this.showSpouseWork) {
        stepCount--
      }

      return stepCount
    },
    availableScopes: function () {
      return ['step1', 'step2', 'step3', 'step4', 'step5', 'step6', 'step7', 'step8', 'step9']
    },
    hasViewed: function () {
      return this.currentCustomer && this.currentCustomer.creditApplicationStatus === 'Viewed'
    }
  },
  methods: {
    // Keep only the first word of a name (e.g. "John Paul" -> "John") ONLY when it overflows maxLength.
    // A two-word name that fits the field limit is left as-is; single-word/hyphenated names are unchanged.
    keepFirstNamePart: function (value, maxLength) {
      if (!value || value.trim().length <= maxLength) return value
      const tokens = value.trim().split(/\s+/).filter(Boolean)
      return tokens.length > 1 ? tokens[0] : value
    },
    // Keep only the last word of a name (e.g. "Da Silva" -> "Silva") ONLY when it overflows maxLength.
    keepLastNamePart: function (value, maxLength) {
      if (!value || value.trim().length <= maxLength) return value
      const tokens = value.trim().split(/\s+/).filter(Boolean)
      return tokens.length > 1 ? tokens[tokens.length - 1] : value
    },
    fetchCreditApplication: async function (currentToken) {
      const that = this
      this.serverErrorMessage = ''
      this.loadingBaseData = true
      this.isLoading = true
      customerService.getCreditApplication(currentToken).then(response => {
        const customer = { ...response.customer }
        // First/last name are read-only on this form (the customer cannot edit them). A two-word name that
        // overflows the DealerTrack field limit (15 first / 25 last) fails the submission, so on entry reduce
        // an over-length two-word first name to its first word and last name to its last word. Two-word names
        // that fit the limit are left as-is.
        customer.firstName = that.keepFirstNamePart(customer.firstName, 15)
        customer.lastName = that.keepLastNamePart(customer.lastName, 25)
        that.currentCustomer = customer
        that.initInputValues()
        that.isLoading = false
        that.isValidToken = true
        if (this.currentCustomer.userId && this.currentCustomer.userId > 0) {
          this.$store.dispatch('user/setCurrentUser', this.currentCustomer.userId)
        }
        if (that.currentCustomer.isCreditApplicationCancelled) {
          that.$router.push({ name: 'CreditApplicationCancelled' })
        } else {
          that.dealerId = that.currentCustomer.dealerId
          if (!that.dealerId || that.dealerId === 0) {
            that.serverErrorMessage = 'Could not load dealer information for customer'
          }
        }
      // eslint-disable-next-line handle-callback-err
      }).catch((error) => {
        that.isLoading = false
        that.serverErrorMessage = 'Could not load customer information with code: ' + currentToken
      })
    },
    fetchCustomer: function (id) {
      const that = this
      this.isLoading = true
      customerService.single(id).then(response => {
        that.isLoading = false
        const customer = { ...response.customer }
        // Same over-length two-word name trim as fetchCreditApplication - both fetch paths hydrate this form.
        customer.firstName = that.keepFirstNamePart(customer.firstName, 15)
        customer.lastName = that.keepLastNamePart(customer.lastName, 25)
        that.currentCustomer = customer
        that.initInputValues()
        that.isValidToken = true
        if (that.currentCustomer.isCreditApplicationCancelled) {
          that.$router.push({ name: 'CreditApplicationCancelled' })
        } else {
          that.dealerId = that.currentCustomer.dealerId
          if (!that.dealerId || that.dealerId === 0) {
            that.serverErrorMessage = 'Could not load dealer information for customer'
          }
        }
      }).catch((error) => {
        that.isLoading = false
        that.serverErrorMessage = error.message
      })
    },
    onRestoreHomePhone: function () {
      this.currentCustomer.home = this.originalHomePhone
    },
    initInputValues () {
      if (this.currentCustomer != null) {
        this.previousCustomerAddresses = []
        this.currentCustomer.addresses.forEach(function (address) {
          var config = {
            sectionId: this.addresses.length + 100,
            loadCustomerOnly: false
          }
          address.config = config
          const checkDate = moment.tz(new Date(), moment.tz.guess()).subtract(10, 'day').format('YYYY-MM-DD')
          const currentDate = moment.tz(new Date(), moment.tz.guess()).format('YYYY-MM-DD')
          const modifiedDate = moment(new Date(address.modifiedAt)).format('YYYY-MM-DD')
          if (modifiedDate >= checkDate && modifiedDate <= currentDate) {
            this.addresses.push(address)
          } else {
            this.previousCustomerAddresses.push(address)
          }
        }, this)
        if (this.currentCustomer.driversFileLocation) {
          var driversPhotoFile = {
            loaded: true,
            imageData: this.currentCustomer.driversFileLocation,
            fileName: this.currentCustomer.drviersFileName,
            fileLocation: this.currentCustomer.driversFileLocation,
            name: this.currentCustomer.driversImageName,
            fileType: this.getFileType(this.currentCustomer.driversFileLocation)
          }

          this.driversPhotoFiles.push(driversPhotoFile)
        }
        if (this.currentCustomer.insuranceFileLocation) {
          var insurancePhotoFile = {
            loaded: true,
            imageData: this.currentCustomer.insuranceFileLocation,
            fileName: this.currentCustomer.insuranceFileName,
            fileLocation: this.currentCustomer.insuranceFileLocation,
            name: this.currentCustomer.insuranceImageName,
            fileType: this.getFileType(this.currentCustomer.insuranceImageName)
          }

          this.insurancePhotoFiles.push(insurancePhotoFile)
        }
        this.workHistory = []
        this.currentCustomer.workHistory.forEach(function (history) {
          var config = {
            sectionId: this.workHistory.length + 100,
            loadCustomerOnly: false
          }
          history.config = config
          this.workHistory.push(history)
        }, this)
        if (this.currentCustomer.spouse) {
          this.spouseAddresses = []
          this.currentCustomer.spouse.addresses.forEach(function (address) {
            var config = {
              sectionId: this.spouseAddresses.length + 100,
              loadCustomerOnly: false
            }
            address.config = config
            this.spouseAddresses.push(address)
          }, this)
          this.spouseWorkHistory = []
          this.currentCustomer.spouse.workHistory.forEach(function (history) {
            var config = {
              sectionId: this.spouseWorkHistory.length + 100,
              loadCustomerOnly: false
            }
            history.config = config
            this.spouseWorkHistory.push(history)
          }, this)
          this.currentSpouseSsn = this.currentCustomer.spouse.ssn
          this.currentCustomer.spouse.ssn = ''
        }
        if (this.addresses.length === 0) {
          this.addAddress({})
        }
        if (this.spouseAddresses.length === 0) {
          this.addAddress({ spouseAddress: true })
        }
        if (this.workHistory.length === 0) {
          this.addWorkHistory()
        }
        if (this.spouseWorkHistory.length === 0) {
          this.addWorkHistory({ spouseWorkHistory: true })
        }

        this.selectedMaritalStatus = this.currentCustomer.maritalStatus
          ? this.definedTypes.customerMaritalStatus.options.find((x) => x.name === this.currentCustomer.maritalStatus)
          : this.definedTypes.customerMaritalStatus.options.find((x) => x.id === 0)
        this.selectedEmploymentStatus = this.currentCustomer.employmentStatus
          ? this.definedTypes.employmentStatus.options.find((x) => x.name === this.currentCustomer.employmentStatus)
          : this.definedTypes.employmentStatus.options.find((x) => x.id === 1)

        if (this.currentCustomer.spouse) {
          const employmentStatus = this.currentCustomer.spouse.employmentStatus
          this.selectedSpouseEmploymentStatus = employmentStatus
            ? this.definedTypes.employmentStatus.options.find((x) => x.name === employmentStatus)
            : this.definedTypes.employmentStatus.options.find((x) => x.id === 1)
        }

        if (this.currentCustomer.hasCoApplicant) {
          this.selectedRelationshipStatus = this.definedTypes.relationshipStatus.options.find((x) => x.name === this.currentCustomer.coApplicationRelation)
        } else {
          this.selectedRelationshipStatus = this.definedTypes.relationshipStatus.options.find((x) => x.id === 5)
        }

        if (!this.hasViewed) {
          this.originalHomePhone = this.currentCustomer.home && this.currentCustomer.home !== '' ? this.currentCustomer.home : ''
          this.currentCustomer.home = ''
        }

        this.currentSsn = this.currentCustomer.ssn
        this.currentCustomer.ssn = ''
      }
    },
    onSave: function () {
      if (!this.geoLocationEnabled && !this.locationModalShown) {
        this.locationModalShown = true
        this.isLocationModalActive = true
      } else {
        this.currentCustomer.isCreditContinueLater = false
        this.save()
      }
    },
    onComplete () {
      if (this.currentCustomer.isCreditContinueLater) {
        this.$router.push({ name: 'CreditApplicationContinueLater' })
      } else {
        this.$router.push({ name: 'CreditApplicationComplete' })
      }
    },
    async saveAndContinueLater () {
      this.currentCustomer.isCreditContinueLater = true
      if (this.spouseInfoRequired) {
        this.currentCustomer.spouse.isCreditContinueLater = true
      }
      this.currentCustomer.url = this.currentUrl
      await this.updateReturnUrl(this.currentUrl)
      await this.updateIsContinueLater(true)
      this.save()
    },
    save () {
      if (!this.currentCustomer.isCreditTermsAccepted) {
        this.$refs.termsControl.showError()
        return
      }
      if (this.communityPropertyDisclosureRequired && !this.currentCustomer.communityPropertyDisclosure) {
        this.$refs.copControl.showError()
        return
      }
      if (this.hasCoApplicant && !this.currentCustomer.regulationB) {
        this.$refs.regBControl.showError()
        return
      }
      // A disclosure left checked for a state that no longer requires it is rejected by
      // DealerTrack. The control is hidden in that case, so clear it on the model here -
      // otherwise the stale value is reposted on every save with no way to undo it.
      // Skipped on "save and continue later": that path runs no validation, so the address
      // state may be unresolved and communityPropertyDisclosureRequired untrustworthy - and
      // a partial save never reaches DealerTrack, so there is nothing to protect there.
      if (!this.currentCustomer.isCreditContinueLater && !this.communityPropertyDisclosureRequired) {
        this.currentCustomer.communityPropertyDisclosure = false
      }
      // Same for Regulation B, but only for a sole applicant. hasCoApplicant is also false when
      // this customer IS the co-applicant, and that record has to keep its own acceptance - so
      // exclude it explicitly rather than relying on hasCoApplicant alone.
      if (!this.currentCustomer.isCreditContinueLater && !this.isCustomerCoApplicant && !this.hasCoApplicant) {
        this.currentCustomer.regulationB = false
      }
      var customerModel = {
        id: this.currentCustomer.id,
        isCreate: false,
        subscriberId: this.currentCustomer.subscriberId,
        dealerId: this.dealerId,
        type: this.currentCustomer.type,
        firstName: this.currentCustomer.firstName,
        middleName: this.currentCustomer.middleName,
        lastName: this.currentCustomer.lastName,
        initials: this.currentCustomer.initials,
        email: this.currentCustomer.email,
        mobile: this.currentCustomer.mobile,
        home: this.currentCustomer.home,
        addresses: [],
        workHistory: [],
        status: this.currentCustomer.status,
        ssn: this.currentCustomer.ssn !== '' ? this.currentCustomer.ssn : this.currentSsn,
        dateOfBirth: this.currentCustomer.dateOfBirth ? moment(this.currentCustomer.dateOfBirth).format('MM/DD/YYYY') : null,
        isCreditTermsAccepted: this.currentCustomer.isCreditTermsAccepted,
        maritalStatus: this.selectedMaritalStatus ? this.selectedMaritalStatus.name : null,
        employmentStatus: this.selectedEmploymentStatus ? this.selectedEmploymentStatus.name : null,
        relationshipType: this.selectedRelationshipStatus ? this.selectedRelationshipStatus.name : null,
        defaultPaymentMethod: this.currentCustomer.defaultPaymentMethod,
        spouseId: this.currentCustomer.spouseId,
        isContinueLater: this.currentCustomer.isContinueLater,
        communityPropertyDisclosure: this.currentCustomer.communityPropertyDisclosure,
        regulationB: this.currentCustomer.regulationB,
        url: this.currentCustomer.url,
        spouse: {},
        coApplicant: {},
        isCreditContinueLater: this.currentCustomer.isCreditContinueLater,
        latestCreditApplicationId: this.currentCustomer.latestCreditApplicationId,
        isSpouseCoApplicant: this.isCoApplicantSpouse,
        uploadName: '',
        uniqueId: '',
        driversUploadName: '',
        driversUniqueId: '',
        insuranceUploadName: '',
        insuranceUniqueId: '',
        customerId: null,
        leadId: null,
        agentUserId: null,
        agentLocationId: 0,
        ageInMonths: this.calculateAgeInMonths,
        userId: this.currentCustomer.userId
      }

      this.saveAddress(customerModel)
      this.saveWorkHistory(customerModel)

      if (this.spouseInfoRequired) {
        var spouseCustomerModel = {
          id: this.currentCustomer.spouse.id,
          isCreate: false,
          subscriberId: this.currentCustomer.spouse.subscriberId,
          dealerId: this.dealerId,
          type: this.currentCustomer.spouse.type,
          firstName: this.$lodash.startCase(this.currentCustomer.spouse.firstName),
          lastName: this.$lodash.startCase(this.currentCustomer.spouse.lastName),
          email: this.currentCustomer.spouse.email,
          mobile: this.currentCustomer.spouse.mobile,
          home: this.currentCustomer.spouse.home,
          addresses: [],
          workHistory: [],
          status: this.currentCustomer.spouse.status,
          ssn: this.currentCustomer.spouse.ssn !== '' ? this.currentCustomer.spouse.ssn : this.currentSpouseSsn,
          dateOfBirth: moment(this.currentCustomer.spouse.dateOfBirth).format('MM/DD/YYYY'),
          defaultPaymentMethod: this.currentCustomer.spouse.defaultPaymentMethod,
          maritalStatus: this.currentCustomer.selectedMaritalStatus ? this.currentCustomer.selectedMaritalStatus.name : null,
          employmentStatus: this.selectedSpouseEmploymentStatus.name,
          isContinueLater: this.currentCustomer.spouse.isContinueLater,
          isCreditContinueLater: this.currentCustomer.spouse.isCreditContinueLater,
          ageInMonths: this.calculateSpouseAgeInMonths,
          userId: this.currentCustomer.userId
        }
        customerModel.spouse = spouseCustomerModel
        this.saveSpouseAddress(customerModel.spouse)
        this.saveSpouseWorkHistory(customerModel.spouse)
      }

      customerModel.uploadName = this.currentCustomer.uploadName
      customerModel.uniqueId = this.currentCustomer.uniqueId

      if (this.driversPhotoFiles.length > 0) {
        var driversPhotoFile = this.driversPhotoFiles.filter((y) => !y.id || y.id <= 0).map((x) => {
          return {
            uploadName: x.name,
            uniqueId: x.uniqueId
          }
        })

        customerModel.driversUploadName = driversPhotoFile[0].uploadName
        customerModel.driversUniqueId = driversPhotoFile[0].uniqueId
      } else {
        customerModel.driversUploadName = ''
        customerModel.driversUniqueId = ''
      }

      if (this.insurancePhotoFiles.length > 0) {
        var insurancePhotoFile = this.insurancePhotoFiles.filter((y) => !y.id || y.id <= 0).map((x) => {
          return {
            uploadName: x.name,
            uniqueId: x.uniqueId
          }
        })

        customerModel.insuranceUploadName = insurancePhotoFile[0].uploadName
        customerModel.insuranceUniqueId = insurancePhotoFile[0].uniqueId
      } else {
        customerModel.insuranceUploadName = ''
        customerModel.insuranceUniqueId = ''
      }

      if (this.hasCoApplicant && !this.isCoApplicantSpouse) {
        var coApplicantCustomerModel = {
          id: this.currentCustomer.coApplicant.id,
          dealerId: 0,
          type: 'Individual',
          status: 'Active',
          firstName: this.$lodash.startCase(this.currentCustomer.coApplicant.firstName),
          lastName: this.$lodash.startCase(this.currentCustomer.coApplicant.lastName),
          email: this.currentCustomer.coApplicant.email,
          mobile: this.currentCustomer.coApplicant.mobile,
          home: this.currentCustomer.coApplicant.home,
          dateOfBirth: moment(this.currentCustomer.coApplicant.dateOfBirth).format('MM/DD/YYYY')
        }
        customerModel.coApplicant = coApplicantCustomerModel
      }

      customerModel.customerId = this.currentCustomer.customerId
      customerModel.leadId = this.currentCustomer.leadId
      customerModel.agentUserId = this.currentCustomer.agentUserId
      customerModel.agentLocationId = this.currentCustomer.agentLocationId

      this.isSaving = true
      this.serverErrorMessage = ''
      this.serverErrors = []
      customerService.update(customerModel).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
          this.$nextTick(function () {
            var elmnt = document.getElementById('main-page')
            elmnt.scrollTo(0, 0)
          })
        } else {
          this.$emit('on-save', response.customer)
          this.onChangesSaved()
          this.onComplete()
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
        this.$nextTick(function () {
          var elmnt = document.getElementById('main-page')
          elmnt.scrollTo(0, 0)
        })
      })
    },
    async navigateToBusiness () {
      this.$router.push({ name: 'BusinessCreditApplication', query: { token: this.token } })
    },
    onLoadCoApplicant: function (customer) {
      this.currentCustomer.coApplicant = customer
      this.$refs.customer_lookup.changeModalState(false)
    },
    onNoCoApplicant: function (customer) {
      this.currentCustomer.coApplicant.mobile = customer ? customer.mobile : ''
      this.currentCustomer.coApplicant.email = customer ? customer.email : ''
      this.currentCustomer.coApplicant.ssn = customer ? customer.ssn : ''
      this.currentCustomer.coApplicant.dateOfBirth = customer ? customer.dateOfBirth : ''
      this.$refs.customer_lookup.changeModalState(false)
    }
  },
  created () {
    this.currentUrl = window.location.href
    if (!('geolocation' in navigator)) {
      this.geoLocationEnabled = false
    } else {
      this.geoLocationEnabled = true
    }
    this.$emit('is-home', false)
  },
  watch: {
    token: function (newValue, oldValue) {
      this.isValidToken = false
    },
    selectedRelationshipStatus: function (newValue, oldValue) {
      if (newValue) {
        if (this.hasCoApplicant && !this.isCoApplicantSpouse) {
          this.$refs.customer_lookup.changeModalState(true)
        }
      }
    }
  },
  mounted: function () {
    this.bulmaSteps = bulmaSteps.attach('.steps', {
      stepClickable: this.isUser || this.isMember,
      onValidateStep: (a, b, e) => {
        this.validateStep(e)
      },
      beforeNext: id => {
        var beforeNextResult = {}
        if (this.preventClick) {
          beforeNextResult.errors = ['Validation Failed']
          return beforeNextResult
        } else {
          switch (this.currentStep) {
            case 0:
              // beforeNextResult.errors = this.validateHomePhone()
              // if (beforeNextResult.errors.length === 0) {
              //   if (!this.showCoAppStep) {
              //     beforeNextResult.skipCount = 1
              //   }
              // }
              // return beforeNextResult
              if (!this.showCoAppStep) {
                beforeNextResult.skipCount = 1
              }
              return beforeNextResult
            case 2:
              beforeNextResult.errors = this.validateResidenceHistory()
              if (beforeNextResult.errors.length === 0 && !this.showWorkStep) {
                if (!this.showSpouseStep) {
                  beforeNextResult.skipCount = 4
                } else {
                  beforeNextResult.skipCount = 1
                }
              }
              return beforeNextResult
            case 3:
              beforeNextResult.errors = this.validateWorkHistory()
              if (beforeNextResult.errors.length === 0 && this.showWorkStep) {
                if (!this.showSpouseStep) {
                  beforeNextResult.skipCount = 3
                }
              }
              return beforeNextResult
            case 5:
              beforeNextResult.errors = this.validateSpouseResidenceHistory()
              if (beforeNextResult.errors.length === 0 && !this.showSpouseWork) {
                if (this.showSpouseStep) {
                  beforeNextResult.skipCount = 1
                }
              }
              return beforeNextResult
            case 6:
              beforeNextResult.errors = this.validateSpouseWorkHistory()
              return beforeNextResult
          }
          return beforeNextResult
        }
      },
      beforePrevious: id => {
        this.currentStep = id
        let skipCount = 0

        switch (this.currentStep) {
          case 2:
            if (!this.showCoAppStep) {
              skipCount = 1
            }
            return skipCount
          case 3:
            if (!this.showWorkStep) {
              skipCount = 1
            }
            return skipCount
          case 4:
            if (this.showSpouseStep && !this.showWorkStep) {
              skipCount = 1
            }
            return skipCount
          case 7:
            if (!this.showWorkStep) {
              if (!this.showSpouseStep) {
                skipCount = 4
              } else {
                if (!this.showSpouseWork) {
                  skipCount = 1
                }
              }
            } else {
              if (!this.showSpouseStep) {
                skipCount = 3
              } else {
                if (!this.showSpouseWork) {
                  skipCount = 1
                }
              }
            }
            return skipCount
        }
      },
      onShow: id => {
        var elmnt = document.getElementById('main-page')
        elmnt.scrollTo(0, 0)
        this.currentStep = id
      }
    })
    this.currentCustomerId = this.$route.params.id
    if (this.currentCustomerId && this.currentCustomerId > 0) {
      this.fetchCustomer(this.currentCustomerId)
    } else {
      this.fetchCreditApplication(this.token)
    }
    this.$emit('set-title-one', 'CUSTOMER CREDIT')
    this.$emit('set-title-two', 'APPLICATION')
  }
}

</script>

<style scoped>
  .customButtons {
    display: flex !important;
    flex-basis: 0 !important;
    flex-grow: 1 !important;
    margin: .5rem !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .customButtonContainer {
    display: flex !important;
    align-items: stretch !important;
    flex-basis: 100% !important;
  }

  .box {
    background-color: #f6f8fa;
  }

  .is-danger > .el-input__inner:hover {
    border-color: #ff0000 !important;
  }
</style>
